import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import useResizeObserver from 'use-resize-observer';
import cn from 'clsx';
import BaseButton from '../buttons/BaseButton';
import { ArrowLeftSLine, ArrowRightSLine } from '../icons';
import { apiClasses, dataTestIds } from './definitions';
import { DaySelectPropTypes } from './types';
import Option from './Option';
import { useDaySelectStyles } from './styles';

const clamp = (min = 0, value = 0, max = 0) => {
    return Math.min(Math.max(value, min), max);
};

const DaySelect: React.FC<DaySelectPropTypes> = ({ data, selected, onChange, className }) => {
    const classes = useDaySelectStyles();
    const frameRef = useRef<HTMLDivElement>(null);
    const { width: frameWidth = 0 } = useResizeObserver<HTMLDivElement>({ ref: frameRef });

    const [frameStart, setFrameStart] = useState(0);
    const frameEnd = useMemo(() => frameStart + frameWidth, [frameStart, frameWidth]);

    const applyVisibleFrameStart = useCallback(() => {
        if (!frameRef.current) {
            return;
        }
        frameRef.current.scrollLeft = frameStart;
    }, [frameStart]);
    useEffect(applyVisibleFrameStart, [frameStart, applyVisibleFrameStart]);

    const moveToNextPage = useCallback(
        (increment = 1) => {
            if (!frameRef.current) {
                return;
            }
            const nextPageStart = frameStart + frameWidth * increment;
            const lastPageStart = frameRef.current?.scrollWidth - frameWidth;
            setFrameStart(clamp(0, nextPageStart, lastPageStart));
        },
        [frameStart, frameWidth]
    );

    const nudgeIntoView = useCallback(
        (itemStart: number, itemWidth: number) => {
            let newFrameStart: number | undefined;

            const isTooFarRight = itemStart + itemWidth > frameEnd;
            const isTooFarLeft = itemStart < frameStart;

            if (isTooFarRight) {
                newFrameStart = itemStart + itemWidth - frameWidth;
            }
            if (isTooFarLeft || !frameWidth) {
                newFrameStart = itemStart;
            }

            if (typeof newFrameStart !== 'undefined') {
                setFrameStart(newFrameStart);
            }
        },
        [frameStart, frameEnd, frameWidth]
    );

    const isFrameTooNarrow = useMemo(
        () => frameRef.current && (!frameWidth || frameWidth < frameRef.current?.scrollWidth),
        [frameWidth]
    );

    const isLeftArrowEnabled = frameStart > 0;
    const isRightArrowEnabled = frameRef.current && frameEnd < frameRef.current?.scrollWidth;
    return (
        <div className={cn(classes.container, className)}>
            {isFrameTooNarrow && (
                <>
                    <BaseButton
                        className={cn(!isLeftArrowEnabled && classes.disabled)}
                        onClick={() => moveToNextPage(-1)}
                    >
                        <ArrowLeftSLine
                            className={cn(classes.arrowIcon)}
                            width="20px"
                            height="20px"
                        />
                    </BaseButton>
                    {isLeftArrowEnabled && <div className={classes.left} />}
                </>
            )}
            <div
                aria-label="Day select"
                className={classes.select}
                data-class={apiClasses.daySelect}
                data-testid={dataTestIds.daySelect}
                ref={frameRef}
                role="listbox"
            >
                {data.map(({ value, label }) => (
                    <Option
                        key={value}
                        label={label}
                        onClick={onChange}
                        onSelected={nudgeIntoView}
                        selected={selected === value}
                        value={value}
                    />
                ))}
            </div>
            {isFrameTooNarrow && (
                <>
                    {isRightArrowEnabled && <div className={classes.right} />}
                    <BaseButton
                        className={cn(!isRightArrowEnabled && classes.disabled)}
                        onClick={() => moveToNextPage()}
                    >
                        <ArrowRightSLine
                            className={cn(
                                classes.arrowIcon,
                                !isRightArrowEnabled && classes.disabled
                            )}
                            width="20px"
                            height="20px"
                        />
                    </BaseButton>
                </>
            )}
        </div>
    );
};
export default DaySelect;
